import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Edit2, Trash2, Utensils, AlertTriangle, CheckCircle, Clock, AlertCircle, Loader2, Save, X } from 'lucide-react';
import { petsService, PetDietNutrition, Pet } from '../../lib/petsService';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Modal } from '../ui/modal';

interface DietNutritionProps {
  selectedPetId?: string;
}

export function DietNutrition({ selectedPetId }: DietNutritionProps) {
  const [pets, setPets] = useState<Pet[]>([]);
  const [dietRecords, setDietRecords] = useState<PetDietNutrition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<PetDietNutrition | null>(null);
  const [selectedPet, setSelectedPet] = useState<Pet | null>(null);
  const [deletingRecordId, setDeletingRecordId] = useState<string | null>(null);

  const foodTypeOptions = [
    { value: 'dry', label: 'Dry Food' },
    { value: 'wet', label: 'Wet Food' },
    { value: 'raw', label: 'Raw Food' },
    { value: 'homemade', label: 'Homemade' },
    { value: 'treats', label: 'Treats' },
    { value: 'supplements', label: 'Supplements' }
  ];

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [petsData, dietData] = await Promise.all([
        petsService.getPets(),
        selectedPetId ? petsService.getPetDietNutrition(selectedPetId) : petsService.getPetDietNutrition()
      ]);
      
      setPets(petsData);
      setDietRecords(dietData);
      
      if (selectedPetId) {
        const pet = petsData.find(p => p.id === selectedPetId);
        setSelectedPet(pet || null);
      }
    } catch (err) {
      console.error('Error fetching diet data:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRecord = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this diet record?')) {
      return;
    }

    try {
      setDeletingRecordId(id);
      await petsService.deletePetDietNutrition(id);
      setDietRecords(dietRecords.filter(record => record.id !== id));
    } catch (err) {
      console.error('Error deleting diet record:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete diet record');
    } finally {
      setDeletingRecordId(null);
    }
  };

  const handleEditRecord = (record: PetDietNutrition) => {
    setSelectedRecord(record);
    setShowEditModal(true);
  };

  const handleAddSuccess = () => {
    fetchData();
    setShowAddModal(false);
  };

  const handleEditSuccess = () => {
    fetchData();
    setShowEditModal(false);
    setSelectedRecord(null);
  };

  const getPetName = (petId: string) => {
    const pet = pets.find(p => p.id === petId);
    return pet?.name || 'Unknown Pet';
  };

  const getFoodTypeColor = (foodType: string) => {
    const colors: { [key: string]: string } = {
      dry: 'bg-blue-50 text-blue-600 border-blue-200',
      wet: 'bg-green-50 text-green-600 border-green-200',
      raw: 'bg-orange-50 text-orange-600 border-orange-200',
      homemade: 'bg-purple-50 text-purple-600 border-purple-200',
      treats: 'bg-pink-50 text-pink-600 border-pink-200',
      supplements: 'bg-cyan-50 text-cyan-600 border-cyan-200',
      default: 'bg-gray-50 text-gray-600 border-gray-200'
    };
    return colors[foodType] || colors.default;
  };

  useEffect(() => {
    fetchData();
  }, [selectedPetId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 size={48} className="animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading diet records...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Diet & Nutrition</h2>
          <p className="text-gray-600 mt-1">
            {selectedPet 
              ? `Manage ${selectedPet.name}'s diet and nutrition` 
              : 'Manage your pets\' diet and nutrition information'
            }
          </p>
        </div>
        <Button
          onClick={() => setShowAddModal(true)}
          className="bg-green-600 hover:bg-green-700 text-white"
        >
          <Plus size={20} className="mr-2" />
          Add Diet Record
        </Button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center">
            <AlertCircle size={20} className="text-red-600 mr-2" />
            <p className="text-red-800">{error}</p>
          </div>
        </div>
      )}

      {/* Diet Records Grid */}
      {dietRecords.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {dietRecords.map((record) => (
            <motion.div
              key={record.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Card className="overflow-hidden hover:shadow-xl transition-all duration-300 group">
                {/* Header */}
                <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-green-50 to-emerald-50">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <Utensils size={20} className="text-green-600" />
                      <h3 className="text-lg font-semibold text-gray-900">{record.food_name}</h3>
                    </div>
                    <div className="flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditRecord(record)}
                        className="text-gray-600 hover:text-gray-800"
                      >
                        <Edit2 size={16} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteRecord(record.id)}
                        disabled={deletingRecordId === record.id}
                        className="text-red-600 hover:text-red-700"
                      >
                        {deletingRecordId === record.id ? (
                          <Loader2 size={16} className="animate-spin" />
                        ) : (
                          <Trash2 size={16} />
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* Pet Name */}
                  <p className="text-sm text-gray-600 mb-2">
                    For: <span className="font-medium">{getPetName(record.pet_id)}</span>
                  </p>

                  {/* Food Type Badge */}
                  {record.food_type && (
                    <div className={`inline-block px-3 py-1 rounded-full text-sm font-medium border ${getFoodTypeColor(record.food_type)}`}>
                      {record.food_type.charAt(0).toUpperCase() + record.food_type.slice(1)}
                    </div>
                  )}
                </div>

                {/* Details */}
                <div className="p-6 space-y-3">
                  {record.brand && (
                    <div className="flex items-center text-sm text-gray-600">
                      <span className="font-medium mr-2">Brand:</span>
                      {record.brand}
                    </div>
                  )}

                  {record.serving_size && (
                    <div className="flex items-center text-sm text-gray-600">
                      <span className="font-medium mr-2">Serving Size:</span>
                      {record.serving_size}
                    </div>
                  )}

                  {record.frequency && (
                    <div className="flex items-center text-sm text-gray-600">
                      <Clock size={16} className="mr-2 text-gray-400" />
                      <span className="font-medium mr-2">Frequency:</span>
                      {record.frequency}
                    </div>
                  )}

                  {record.special_diet && (
                    <div className="flex items-center text-sm text-amber-600 bg-amber-50 px-3 py-2 rounded-lg">
                      <AlertTriangle size={16} className="mr-2" />
                      Special Diet
                    </div>
                  )}

                  {record.dietary_restrictions && record.dietary_restrictions.length > 0 && (
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-gray-700">Dietary Restrictions:</p>
                      <div className="flex flex-wrap gap-1">
                        {record.dietary_restrictions.map((restriction, index) => (
                          <span key={index} className="px-2 py-1 bg-red-50 text-red-700 text-xs rounded-full">
                            {restriction}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {record.allergies && record.allergies.length > 0 && (
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-gray-700">Allergies:</p>
                      <div className="flex flex-wrap gap-1">
                        {record.allergies.map((allergy, index) => (
                          <span key={index} className="px-2 py-1 bg-orange-50 text-orange-700 text-xs rounded-full">
                            {allergy}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {record.notes && (
                    <div className="pt-3 border-t border-gray-100">
                      <p className="text-sm text-gray-600 italic">"{record.notes}"</p>
                    </div>
                  )}
                </div>

                {/* Quick Actions */}
                <div className="px-6 pb-6">
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1 text-xs"
                      onClick={() => handleEditRecord(record)}
                    >
                      <Edit2 size={14} className="mr-1" />
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1 text-xs"
                      onClick={() => handleDeleteRecord(record.id)}
                      disabled={deletingRecordId === record.id}
                    >
                      {deletingRecordId === record.id ? (
                        <Loader2 size={14} className="mr-1 animate-spin" />
                      ) : (
                        <Trash2 size={14} className="mr-1" />
                      )}
                      Delete
                    </Button>
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      ) : (
        /* Empty State */
        <div className="text-center py-16">
          <div className="w-24 h-24 bg-gradient-to-br from-green-50 to-emerald-50 rounded-full flex items-center justify-center mx-auto mb-6">
            <Utensils size={48} className="text-green-500" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-3">No Diet Records Yet</h3>
          <p className="text-gray-600 mb-8 max-w-md mx-auto">
            {selectedPet 
              ? `Start tracking ${selectedPet.name}'s diet and nutrition information`
              : 'Start tracking your pets\' diet and nutrition information'
            }
          </p>
          <Button
            onClick={() => setShowAddModal(true)}
            className="bg-green-600 hover:bg-green-700 text-white px-8 py-3"
          >
            <Plus size={20} className="mr-2" />
            Add First Diet Record
          </Button>
        </div>
      )}

      {/* Add Diet Record Modal */}
      <AddDietRecordModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={handleAddSuccess}
        pets={pets}
        selectedPetId={selectedPetId}
      />

      {/* Edit Diet Record Modal */}
      <EditDietRecordModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedRecord(null);
        }}
        onSuccess={handleEditSuccess}
        record={selectedRecord}
        pets={pets}
      />
    </div>
  );
}

// Add Diet Record Modal Component
interface AddDietRecordModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  pets: Pet[];
  selectedPetId?: string;
}

function AddDietRecordModal({ isOpen, onClose, onSuccess, pets, selectedPetId }: AddDietRecordModalProps) {
  const [formData, setFormData] = useState({
    pet_id: selectedPetId || '',
    food_name: '',
    food_type: '',
    brand: '',
    serving_size: '',
    frequency: '',
    special_diet: false,
    dietary_restrictions: [] as string[],
    allergies: [] as string[],
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const foodTypeOptions = [
    { value: 'dry', label: 'Dry Food' },
    { value: 'wet', label: 'Wet Food' },
    { value: 'raw', label: 'Raw Food' },
    { value: 'homemade', label: 'Homemade' },
    { value: 'treats', label: 'Treats' },
    { value: 'supplements', label: 'Supplements' }
  ];

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    const newErrors: Record<string, string> = {};
    if (!formData.pet_id) newErrors.pet_id = 'Please select a pet';
    if (!formData.food_name.trim()) newErrors.food_name = 'Food name is required';
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      await petsService.createPetDietNutrition(formData);
      onSuccess();
    } catch (error) {
      setErrors({ general: error instanceof Error ? error.message : 'Failed to add diet record' });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      pet_id: selectedPetId || '',
      food_name: '',
      food_type: '',
      brand: '',
      serving_size: '',
      frequency: '',
      special_diet: false,
      dietary_restrictions: [],
      allergies: [],
      notes: ''
    });
    setErrors({});
  };

  const handleClose = () => {
    if (!loading) {
      resetForm();
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Add Diet Record">
      <form onSubmit={handleSubmit} className="space-y-6">
        {errors.general && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center">
              <AlertCircle size={20} className="text-red-600 mr-2" />
              <p className="text-red-800">{errors.general}</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="pet_id" className="text-sm font-medium text-gray-700">
              Pet *
            </Label>
            <Select
              value={formData.pet_id}
              onValueChange={(value) => handleInputChange('pet_id', value)}
            >
              <SelectTrigger className={errors.pet_id ? 'border-red-500' : ''}>
                <SelectValue placeholder="Select pet" />
              </SelectTrigger>
              <SelectContent>
                {pets.map(pet => (
                  <SelectItem key={pet.id} value={pet.id}>
                    {pet.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.pet_id && (
              <p className="text-sm text-red-600">{errors.pet_id}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="food_name" className="text-sm font-medium text-gray-700">
              Food Name *
            </Label>
            <Input
              id="food_name"
              value={formData.food_name}
              onChange={(e) => handleInputChange('food_name', e.target.value)}
              placeholder="Enter food name"
              className={errors.food_name ? 'border-red-500' : ''}
            />
            {errors.food_name && (
              <p className="text-sm text-red-600">{errors.food_name}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="food_type" className="text-sm font-medium text-gray-700">
              Food Type
            </Label>
            <Select
              value={formData.food_type}
              onValueChange={(value) => handleInputChange('food_type', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select food type" />
              </SelectTrigger>
              <SelectContent>
                {foodTypeOptions.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="brand" className="text-sm font-medium text-gray-700">
              Brand
            </Label>
            <Input
              id="brand"
              value={formData.brand}
              onChange={(e) => handleInputChange('brand', e.target.value)}
              placeholder="Enter brand name"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="serving_size" className="text-sm font-medium text-gray-700">
              Serving Size
            </Label>
            <Input
              id="serving_size"
              value={formData.serving_size}
              onChange={(e) => handleInputChange('serving_size', e.target.value)}
              placeholder="e.g., 1 cup, 100g"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="frequency" className="text-sm font-medium text-gray-700">
              Frequency
            </Label>
            <Input
              id="frequency"
              value={formData.frequency}
              onChange={(e) => handleInputChange('frequency', e.target.value)}
              placeholder="e.g., Twice daily, Once a week"
            />
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="special_diet"
              checked={formData.special_diet}
              onChange={(e) => handleInputChange('special_diet', e.target.checked)}
              className="rounded border-gray-300"
            />
            <Label htmlFor="special_diet" className="text-sm font-medium text-gray-700">
              Special Diet
            </Label>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes" className="text-sm font-medium text-gray-700">
              Notes
            </Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              placeholder="Enter any additional notes about this diet"
              rows={3}
            />
          </div>
        </div>

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={loading}
            className="bg-green-600 hover:bg-green-700 text-white"
          >
            {loading ? (
              <>
                <Loader2 size={16} className="mr-2 animate-spin" />
                Adding...
              </>
            ) : (
              <>
                <Save size={16} className="mr-2" />
                Add Record
              </>
            )}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// Edit Diet Record Modal Component
interface EditDietRecordModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  record: PetDietNutrition | null;
  pets: Pet[];
}

function EditDietRecordModal({ isOpen, onClose, onSuccess, record, pets }: EditDietRecordModalProps) {
  const [formData, setFormData] = useState({
    pet_id: '',
    food_name: '',
    food_type: '',
    brand: '',
    serving_size: '',
    frequency: '',
    special_diet: false,
    dietary_restrictions: [] as string[],
    allergies: [] as string[],
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const foodTypeOptions = [
    { value: 'dry', label: 'Dry Food' },
    { value: 'wet', label: 'Wet Food' },
    { value: 'raw', label: 'Raw Food' },
    { value: 'homemade', label: 'Homemade' },
    { value: 'treats', label: 'Treats' },
    { value: 'supplements', label: 'Supplements' }
  ];

  useEffect(() => {
    if (record) {
      setFormData({
        pet_id: record.pet_id,
        food_name: record.food_name,
        food_type: record.food_type || '',
        brand: record.brand || '',
        serving_size: record.serving_size || '',
        frequency: record.frequency || '',
        special_diet: record.special_diet,
        dietary_restrictions: record.dietary_restrictions || [],
        allergies: record.allergies || [],
        notes: record.notes || ''
      });
    }
  }, [record]);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!record) return;

    // Validation
    const newErrors: Record<string, string> = {};
    if (!formData.pet_id) newErrors.pet_id = 'Please select a pet';
    if (!formData.food_name.trim()) newErrors.food_name = 'Food name is required';
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      await petsService.updatePetDietNutrition(record.id, formData);
      onSuccess();
    } catch (error) {
      setErrors({ general: error instanceof Error ? error.message : 'Failed to update diet record' });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setErrors({});
      onClose();
    }
  };

  if (!record) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Edit Diet Record">
      <form onSubmit={handleSubmit} className="space-y-6">
        {errors.general && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center">
              <AlertCircle size={20} className="text-red-600 mr-2" />
              <p className="text-red-800">{errors.general}</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="pet_id" className="text-sm font-medium text-gray-700">
              Pet *
            </Label>
            <Select
              value={formData.pet_id}
              onValueChange={(value) => handleInputChange('pet_id', value)}
            >
              <SelectTrigger className={errors.pet_id ? 'border-red-500' : ''}>
                <SelectValue placeholder="Select pet" />
              </SelectTrigger>
              <SelectContent>
                {pets.map(pet => (
                  <SelectItem key={pet.id} value={pet.id}>
                    {pet.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.pet_id && (
              <p className="text-sm text-red-600">{errors.pet_id}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="food_name" className="text-sm font-medium text-gray-700">
              Food Name *
            </Label>
            <Input
              id="food_name"
              value={formData.food_name}
              onChange={(e) => handleInputChange('food_name', e.target.value)}
              placeholder="Enter food name"
              className={errors.food_name ? 'border-red-500' : ''}
            />
            {errors.food_name && (
              <p className="text-sm text-red-600">{errors.food_name}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="food_type" className="text-sm font-medium text-gray-700">
              Food Type
            </Label>
            <Select
              value={formData.food_type}
              onValueChange={(value) => handleInputChange('food_type', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select food type" />
              </SelectTrigger>
              <SelectContent>
                {foodTypeOptions.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="brand" className="text-sm font-medium text-gray-700">
              Brand
            </Label>
            <Input
              id="brand"
              value={formData.brand}
              onChange={(e) => handleInputChange('brand', e.target.value)}
              placeholder="Enter brand name"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="serving_size" className="text-sm font-medium text-gray-700">
              Serving Size
            </Label>
            <Input
              id="serving_size"
              value={formData.serving_size}
              onChange={(e) => handleInputChange('serving_size', e.target.value)}
              placeholder="e.g., 1 cup, 100g"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="frequency" className="text-sm font-medium text-gray-700">
              Frequency
            </Label>
            <Input
              id="frequency"
              value={formData.frequency}
              onChange={(e) => handleInputChange('frequency', e.target.value)}
              placeholder="e.g., Twice daily, Once a week"
            />
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="special_diet"
              checked={formData.special_diet}
              onChange={(e) => handleInputChange('special_diet', e.target.checked)}
              className="rounded border-gray-300"
            />
            <Label htmlFor="special_diet" className="text-sm font-medium text-gray-700">
              Special Diet
            </Label>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes" className="text-sm font-medium text-gray-700">
              Notes
            </Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              placeholder="Enter any additional notes about this diet"
              rows={3}
            />
          </div>
        </div>

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={loading}
            className="bg-green-600 hover:bg-green-700 text-white"
          >
            {loading ? (
              <>
                <Loader2 size={16} className="mr-2 animate-spin" />
                Updating...
              </>
            ) : (
              <>
                <Save size={16} className="mr-2" />
                Update Record
              </>
            )}
          </Button>
        </div>
      </form>
    </Modal>
  );
}